/**
 * First-touch signup attribution.
 *
 * Remembers where a visitor first arrived from (referrer host, UTM tags, the
 * path they landed on) so registration can report it, even when they browse
 * around or come back days later before signing up. The API decides the channel
 * and what to keep (web3pwn-api/web3pwn_api/attribution.py); this only collects.
 *
 * Only the referrer's host is kept: a full referrer URL can carry search terms
 * or someone else's private link.
 */

export interface Attribution {
    referrer: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
    landing_path: string | null;
    captured_at: number;
}

export const ATTRIBUTION_KEY = 'signupAttribution';
// A touch this old no longer explains a signup.
export const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const OWN_HOST_SUFFIX = 'app.web3pwn.com';

const referrerHost = (referrer: string, currentHost: string): string | null => {
    if (!referrer) return null;
    try {
        const host = new URL(referrer).hostname.toLowerCase();
        if (!host || host === currentHost || host.endsWith(OWN_HOST_SUFFIX)) return null;
        return host;
    } catch {
        return null;
    }
};

const read = (): Attribution | null => {
    try {
        const raw = localStorage.getItem(ATTRIBUTION_KEY);
        return raw ? (JSON.parse(raw) as Attribution) : null;
    } catch {
        return null;
    }
};

/**
 * Records this page load as the first touch, unless a fresh one is already
 * stored. A visit with UTM tags or an external referrer replaces an older
 * "direct" touch, since it says more about why the person came.
 */
export const captureAttribution = (
    location: Pick<Location, 'search' | 'pathname' | 'hostname'> = window.location,
    referrer: string = document.referrer,
    now: number = Date.now(),
): void => {
    const params = new URLSearchParams(location.search);
    const touch: Attribution = {
        referrer: referrerHost(referrer, location.hostname),
        utm_source: params.get('utm_source'),
        utm_medium: params.get('utm_medium'),
        utm_campaign: params.get('utm_campaign'),
        landing_path: location.pathname,
        captured_at: now,
    };
    const informative = Boolean(touch.referrer || touch.utm_source || touch.utm_campaign);

    const existing = read();
    const existingFresh = existing && now - existing.captured_at < ATTRIBUTION_MAX_AGE_MS;
    const existingInformative = existing && Boolean(existing.referrer || existing.utm_source || existing.utm_campaign);
    if (existingFresh && (existingInformative || !informative)) return;

    try {
        localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(touch));
    } catch {
        // Storage refused (private mode): the signup is simply reported without it.
    }
};

/** What registration sends. Always an object, so the API can tell a new client
 *  with nothing to report ("direct") from an old one ("unknown"). */
export const getAttribution = (): Omit<Attribution, 'captured_at'> => {
    const stored = read();
    return {
        referrer: stored?.referrer ?? null,
        utm_source: stored?.utm_source ?? null,
        utm_medium: stored?.utm_medium ?? null,
        utm_campaign: stored?.utm_campaign ?? null,
        landing_path: stored?.landing_path ?? null,
    };
};
